package features.payment

import features.BaseViewModel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class PaymentViewModel(
    private val repository: PaymentRepository
) : BaseViewModel() {

    // ═══ Listener de confirmación de pago en tiempo real ═══
    private val _pagoConfirmado = MutableStateFlow(false)
    val pagoConfirmado: StateFlow<Boolean> = _pagoConfirmado.asStateFlow()

    private var escuchaPagoJob: kotlinx.coroutines.Job? = null

    /**
     * Inicia un listener realtime sobre pagos_confirmados/{paymentIntentId}.
     * Cuando el webhook de Stripe escribe aquí, la UI se actualiza automáticamente.
     */
    /** Id que se está escuchando. En tarjeta es el cs_id del Checkout (ver espejo del webhook);
     *  la pantalla lo usa para descargar los boletos sin volver a pedirlo. */
    private var idPagoEnEscucha: String = ""
    fun sessionIdTarjeta(): String = idPagoEnEscucha

    fun escucharConfirmacionPago(paymentIntentId: String) {
        if (paymentIntentId.isBlank()) return
        idPagoEnEscucha = paymentIntentId
        escuchaPagoJob?.cancel()
        _pagoConfirmado.value = false
        escuchaPagoJob = viewModelScope.launch {
            try {
                val db = network.FirebaseClient.database
                db.reference("pagos_confirmados/$paymentIntentId").valueEvents.collect { snap ->
                    if (snap.exists) {
                        val status = try { snap.child("status").value<String>() } catch (e: Exception) { "" }
                        if (status == "confirmado" || status == "succeeded") {
                            _pagoConfirmado.value = true
                            escuchaPagoJob?.cancel()
                        }
                    }
                }
            } catch (e: Exception) { /* suscripción cancelada normalmente */ }
        }
    }

    fun detenerEscuchaPago() {
        escuchaPagoJob?.cancel()
        escuchaPagoJob = null
    }

    fun resetPagoConfirmado() {
        _pagoConfirmado.value = false
    }

    /** Guarda la solicitud de factura en Firebase para que el admin la vea */
    private fun guardarSolicitudFactura(
        rfc: String, razonSocial: String, regimenFiscal: String,
        usoCfdi: String, cpFiscal: String, email: String,
        monto: Int, ruta: String, fecha: String, horario: String, asientos: String,
        referenciaPago: String = ""
    ) {
        viewModelScope.launch {
            try {
                val db = network.FirebaseClient.database
                val ref = db.reference("solicitudes_factura").push()
                val datos = mapOf<String, Any>(
                    "rfc" to rfc,
                    "razonSocial" to razonSocial,
                    "regimenFiscal" to regimenFiscal,
                    "usoCfdi" to usoCfdi,
                    "cpFiscal" to cpFiscal,
                    "email" to email,
                    "monto" to monto,
                    "ruta" to ruta,
                    "fecha" to fecha,
                    "horario" to horario,
                    "asientos" to asientos,
                    "timestamp" to org.example.abigail.obtenerMilisegundosActuales().toString(),
                    "estatus" to "pendiente",
                    "paymentIntentId" to referenciaPago
                )
                ref.setValue(datos)
            } catch (e: Exception) { /* best effort */ }
        }
    }

    private val _isProcessing = MutableStateFlow(false)
    val isProcessing: StateFlow<Boolean> = _isProcessing.asStateFlow()

    private val _errorPago = MutableStateFlow<String?>(null)
    val errorPago: StateFlow<String?> = _errorPago.asStateFlow()

    private val _speiReference = MutableStateFlow<String?>(null)
    val speiReference: StateFlow<String?> = _speiReference.asStateFlow()

    private val _speiResult = MutableStateFlow<PaymentRepositoryImpl.SpeiResult?>(null)
    val speiResult: StateFlow<PaymentRepositoryImpl.SpeiResult?> = _speiResult.asStateFlow()

    private val _oxxoResult = MutableStateFlow<PaymentRepositoryImpl.OxxoResult?>(null)
    val oxxoResult: StateFlow<PaymentRepositoryImpl.OxxoResult?> = _oxxoResult.asStateFlow()

    // URL del Checkout de tarjeta. La UI la observa y la abre en el navegador
    // (LocalUriHandler). El VM la limpia inmediatamente después para que no
    // se re-abra en recomposiciones.
    private val _checkoutTarjetaUrl = MutableStateFlow<String?>(null)
    val checkoutTarjetaUrl: StateFlow<String?> = _checkoutTarjetaUrl.asStateFlow()
    fun consumirCheckoutUrl() { _checkoutTarjetaUrl.value = null }

    fun limpiarError() {
        _errorPago.value = null
    }

    /**
     * Genera un Checkout Session de tarjeta en Stripe y expone la URL para que la
     * UI la abra en el navegador. Si Stripe está apagado, delega en el flujo manual
     * (mismo camino que SPEI/OXXO manual: guarda pagos_manuales y muestra la CLABE).
     */
    fun generateCardCheckout(
        monto: Int, ruta: String, fecha: String, horario: String,
        asientos: String, email: String,
        pasajeros: String, asientosArray: List<Int>,
        origenDestino: String, totalPagado: Int,
        puntoSubida: String = "", urlSalida: String = "",
        esIdaYVuelta: Boolean = false, fechaVuelta: String = "",
        horarioVuelta: String = "", rutaVuelta: String = "",
        asientosVueltaArray: List<Int> = emptyList(), pasajerosVuelta: String = "",
        puntoSubidaVuelta: String = "",
        requiereFactura: Boolean = false, rfc: String = "",
        razonSocial: String = "", regimenFiscal: String = "",
        usoCfdi: String = "", cpFiscal: String = ""
    ) {
        viewModelScope.launch {
            _isProcessing.value = true
            _errorPago.value = null

            // Tarjeta SIEMPRE pasa por Stripe: el flag stripeActivo solo controla SPEI
            // (customer_balance de Stripe MX puede fallar en algunas cuentas). Card y OXXO
            // usan Stripe directo sin fallback manual.
            try {
                val res = repository.crearSesionTarjeta(
                    monto, ruta, fecha, horario, asientos, email, pasajeros, asientosArray,
                    origenDestino, totalPagado, puntoSubida, urlSalida, esIdaYVuelta, fechaVuelta,
                    horarioVuelta, rutaVuelta, asientosVueltaArray, pasajerosVuelta, puntoSubidaVuelta,
                    requiereFactura, rfc, razonSocial, regimenFiscal, usoCfdi, cpFiscal
                )
                if (res == null) {
                    _errorPago.value = "No se pudo iniciar el pago con tarjeta. Intenta de nuevo."
                } else {
                    if (requiereFactura) {
                        guardarSolicitudFactura(rfc, razonSocial, regimenFiscal, usoCfdi, cpFiscal, email, res.monto, ruta, fecha, horario, asientos, res.paymentIntentId)
                    }
                    escucharConfirmacionPago(res.paymentIntentId)
                    _checkoutTarjetaUrl.value = res.checkoutUrl
                }
            } catch (e: Exception) {
                _errorPago.value = e.message ?: "Error al iniciar el pago con tarjeta"
            } finally {
                _isProcessing.value = false
            }
        }
    }

    /** Resetear todo el estado de pago al salir del flujo */
    fun resetearEstado() {
        _speiResult.value = null
        _speiReference.value = null
        _oxxoResult.value = null
        _errorPago.value = null
        _isProcessing.value = false
        _pagoConfirmado.value = false
        _esPagoManual.value = false
        _telefonoAtencionPago.value = ""
        detenerEscuchaPago()
    }

    /** true = modo manual (sin Stripe), la CLABE mostrada es la del negocio */
    private val _esPagoManual = MutableStateFlow(false)
    val esPagoManual: StateFlow<Boolean> = _esPagoManual.asStateFlow()

    private val _telefonoAtencionPago = MutableStateFlow("")
    val telefonoAtencionPago: StateFlow<String> = _telefonoAtencionPago.asStateFlow()

    fun generateSpeiReference(
        monto: Int,
        ruta: String,
        fecha: String,
        horario: String,
        asientos: String,
        email: String,
        pasajeros: String,
        asientosArray: List<Int>,
        origenDestino: String,
        totalPagado: Int,
        puntoSubida: String = "",
        urlSalida: String = "",
        esIdaYVuelta: Boolean = false,
        fechaVuelta: String = "",
        horarioVuelta: String = "",
        rutaVuelta: String = "",
        asientosVueltaArray: List<Int> = emptyList(),
        pasajerosVuelta: String = "",
        puntoSubidaVuelta: String = "",
        requiereFactura: Boolean = false,
        rfc: String = "",
        razonSocial: String = "",
        regimenFiscal: String = "",
        usoCfdi: String = "",
        cpFiscal: String = "",
        onSuccess: () -> Unit
    ) {
        viewModelScope.launch {
            _isProcessing.value = true
            _errorPago.value = null

            val stripeActivo = network.ConfigPagos.stripeActivo.value

            if (!stripeActivo) {
                // ═══ MODO MANUAL: sin Stripe ═══
                try {
                    val clabe = network.ConfigPagos.clabeManual.value
                    val beneficiario = network.ConfigPagos.beneficiarioManual.value
                    val banco = network.ConfigPagos.bancoManual.value
                    val telefono = network.ConfigPagos.telefonoAtencion.value

                    if (clabe.isBlank()) {
                        _errorPago.value = "El sistema de pagos no está configurado. Contacta al administrador."
                        _isProcessing.value = false
                        return@launch
                    }

                    // Guardar reserva en pagos_manuales para verificación admin
                    val db = network.FirebaseClient.database
                    val ref = db.reference("pagos_manuales").push()
                    val pagoId = ref.key ?: "manual_${org.example.abigail.obtenerMilisegundosActuales()}"
                    val ahoraMs = org.example.abigail.obtenerMilisegundosActuales()
                    val expiraPagoMs = ahoraMs + 3600000L // 1 hora de plazo
                    val datos = mapOf<String, Any>(
                        "ruta" to ruta,
                        "fecha" to fecha,
                        "horario" to horario,
                        "asientos" to asientos,
                        "email" to email,
                        "totalPagado" to totalPagado,
                        "pasajeros" to pasajeros,
                        // Millis como STRING: un Kotlin Long se serializa en JS como objeto {f3_1,g3_1}
                        // y rompía el cálculo de expiración + el "hace X días".
                        "timestamp" to ahoraMs.toString(),
                        "expiraEn" to expiraPagoMs.toString(),
                        "estatus" to "pendiente",
                        "puntoSubida" to puntoSubida,
                        "metodoPago" to "transferencia_manual",
                        // Tramo de vuelta (ida y vuelta) — para que confirmarPagoManual cree la reserva de regreso
                        "esIdaYVuelta" to esIdaYVuelta,
                        "fechaVuelta" to fechaVuelta,
                        "horarioVuelta" to horarioVuelta,
                        "rutaVuelta" to rutaVuelta,
                        "asientosVuelta" to asientosVueltaArray,
                        "pasajerosVuelta" to pasajerosVuelta,
                        "puntoSubidaVuelta" to puntoSubidaVuelta
                    )
                    ref.setValue(datos)

                    // Nota: CF notificarPagoManualNuevo (onValueCreated en /pagos_manuales)
                    // crea la notificación in-app + envía push admin. Cliente ya no escribe
                    // directo a /notificaciones (rules solo admin/chofer).

                    // Bloquear asientos temporalmente (1 hora). expiraEn como String (Long→objeto en JS).
                    val expiraEn = (org.example.abigail.obtenerMilisegundosActuales() + 3600000L).toString()
                    asientosArray.forEach { asiento ->
                        db.reference("viajes/$ruta/$fecha/$horario/bloqueos/$asiento/pago_pendiente").setValue(
                            mapOf("paymentIntentId" to pagoId, "expiraEn" to expiraEn)
                        )
                    }
                    // Bloquear también los asientos del tramo de vuelta
                    if (esIdaYVuelta && rutaVuelta.isNotBlank() && fechaVuelta.isNotBlank() && horarioVuelta.isNotBlank()) {
                        asientosVueltaArray.forEach { asiento ->
                            db.reference("viajes/$rutaVuelta/$fechaVuelta/$horarioVuelta/bloqueos/$asiento/pago_pendiente").setValue(
                                mapOf("paymentIntentId" to pagoId, "expiraEn" to expiraEn)
                            )
                        }
                    }

                    // Simular SpeiResult con datos manuales
                    _speiResult.value = PaymentRepositoryImpl.SpeiResult(
                        clabe = clabe,
                        banco = banco.ifBlank { "Transferencia Bancaria" },
                        referencia = pagoId,
                        monto = totalPagado,
                        paymentIntentId = pagoId
                    )
                    _speiReference.value = clabe
                    _esPagoManual.value = true
                    _telefonoAtencionPago.value = telefono

                    if (requiereFactura) {
                        guardarSolicitudFactura(rfc, razonSocial, regimenFiscal, usoCfdi, cpFiscal, email, monto, ruta, fecha, horario, asientos, pagoId)
                    }
                    onSuccess()
                } catch (e: Exception) {
                    network.CrashReporter.recordException(e, "PaymentViewModel.generateSpeiReference[manual]")
                    _errorPago.value = "Error al registrar el pago. Intenta de nuevo."
                }
            } else {
                // ═══ MODO STRIPE: flujo normal ═══
                try {
                    val result = repository.crearSesionSPEI(
                        monto = monto,
                        ruta = ruta,
                        fecha = fecha,
                        horario = horario,
                        asientos = asientos,
                        email = email,
                        pasajeros = pasajeros,
                        asientosArray = asientosArray,
                        origenDestino = origenDestino,
                        totalPagado = totalPagado,
                        puntoSubida = puntoSubida,
                        urlSalida = urlSalida,
                        esIdaYVuelta = esIdaYVuelta,
                        fechaVuelta = fechaVuelta,
                        horarioVuelta = horarioVuelta,
                        rutaVuelta = rutaVuelta,
                        asientosVueltaArray = asientosVueltaArray,
                        pasajerosVuelta = pasajerosVuelta,
                        puntoSubidaVuelta = puntoSubidaVuelta,
                        requiereFactura = requiereFactura,
                        rfc = rfc,
                        razonSocial = razonSocial,
                        regimenFiscal = regimenFiscal,
                        usoCfdi = usoCfdi,
                        cpFiscal = cpFiscal
                    )
                    if (result != null) {
                        _speiResult.value = result
                        _speiReference.value = result.clabe
                        _esPagoManual.value = false
                        if (requiereFactura) {
                            guardarSolicitudFactura(rfc, razonSocial, regimenFiscal, usoCfdi, cpFiscal, email, monto, ruta, fecha, horario, asientos, result.paymentIntentId)
                        }
                        onSuccess()
                    } else {
                        _errorPago.value = "No se pudo generar la referencia SPEI. Intenta de nuevo."
                    }
                } catch (e: Exception) {
                    network.CrashReporter.recordException(e, "PaymentViewModel.generateSpeiReference[stripe]")
                    _errorPago.value = "Error de conexión. Verifica tu internet e intenta de nuevo."
                }
            }
            _isProcessing.value = false
        }
    }

    fun generateOxxoReference(
        monto: Int,
        ruta: String,
        fecha: String,
        horario: String,
        asientos: String,
        email: String,
        pasajeros: String,
        asientosArray: List<Int>,
        origenDestino: String,
        totalPagado: Int,
        puntoSubida: String = "",
        urlSalida: String = "",
        esIdaYVuelta: Boolean = false,
        fechaVuelta: String = "",
        horarioVuelta: String = "",
        rutaVuelta: String = "",
        asientosVueltaArray: List<Int> = emptyList(),
        pasajerosVuelta: String = "",
        puntoSubidaVuelta: String = "",
        requiereFactura: Boolean = false,
        rfc: String = "",
        razonSocial: String = "",
        regimenFiscal: String = "",
        usoCfdi: String = "",
        cpFiscal: String = "",
        onSuccess: () -> Unit
    ) {
        viewModelScope.launch {
            _isProcessing.value = true
            _errorPago.value = null
            try {
                val result = repository.crearSesionOxxo(
                    monto = monto,
                    ruta = ruta,
                    fecha = fecha,
                    horario = horario,
                    asientos = asientos,
                    email = email,
                    pasajeros = pasajeros,
                    asientosArray = asientosArray,
                    origenDestino = origenDestino,
                    totalPagado = totalPagado,
                    puntoSubida = puntoSubida,
                    urlSalida = urlSalida,
                    esIdaYVuelta = esIdaYVuelta,
                    fechaVuelta = fechaVuelta,
                    horarioVuelta = horarioVuelta,
                    rutaVuelta = rutaVuelta,
                    asientosVueltaArray = asientosVueltaArray,
                    pasajerosVuelta = pasajerosVuelta,
                    puntoSubidaVuelta = puntoSubidaVuelta,
                    requiereFactura = requiereFactura,
                    rfc = rfc,
                    razonSocial = razonSocial,
                    regimenFiscal = regimenFiscal,
                    usoCfdi = usoCfdi,
                    cpFiscal = cpFiscal
                )
                if (result != null) {
                    _oxxoResult.value = result
                    if (requiereFactura) {
                        guardarSolicitudFactura(rfc, razonSocial, regimenFiscal, usoCfdi, cpFiscal, email, monto, ruta, fecha, horario, asientos, result.paymentIntentId)
                    }
                    onSuccess()
                } else {
                    _errorPago.value = "No se pudo generar la ficha OXXO. Intenta de nuevo."
                }
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "PaymentViewModel.generateOxxoReference")
                _errorPago.value = "Error de conexión. Verifica tu internet e intenta de nuevo."
            }
            _isProcessing.value = false
        }
    }
}
